import { useState } from 'react';
import Modal from './Modal';
import { forgotPassword, verifyResetCode, resetPassword } from '../api/client';
import './ForgotPasswordModal.css';

const TITLES = {
  identify: 'Forgot password',
  verify: 'Enter verification code',
  reset: 'Set a new password',
  done: 'Password updated',
};

export default function ForgotPasswordModal({ onClose }) {
  const [step, setStep] = useState('identify');
  const [identifier, setIdentifier] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleIdentify(e) {
    e.preventDefault();
    if (!identifier.trim()) return;
    setLoading(true);
    setError('');
    try {
      const data = await forgotPassword(identifier.trim());
      setMaskedEmail(data.email || '');
      setStep('verify');
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(e) {
    e.preventDefault();
    if (!otp.trim()) return;
    setLoading(true);
    setError('');
    try {
      const data = await verifyResetCode(identifier.trim(), otp.trim());
      setResetToken(data.resetToken);
      setStep('reset');
    } catch (err) {
      setError(err.message || 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  }

  async function handleReset(e) {
    e.preventDefault();
    if (!password || !confirmPassword) return;
    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await resetPassword(identifier.trim(), resetToken, password);
      setStep('done');
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal title={TITLES[step]} onClose={onClose}>
      {step === 'identify' && (
        <form onSubmit={handleIdentify}>
          <p className="forgot-modal-text">Enter your username or email and we'll send you a verification code.</p>
          <label className="forgot-field">
            <span>Username or email</span>
            <input
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              disabled={loading}
              autoFocus
              autoComplete="username"
            />
          </label>
          {error && <p className="forgot-error">{error}</p>}
          <div className="forgot-actions">
            <button type="button" className="forgot-btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="forgot-btn-primary" disabled={loading}>
              {loading ? 'Sending…' : 'Send code'}
            </button>
          </div>
        </form>
      )}

      {step === 'verify' && (
        <form onSubmit={handleVerify}>
          <p className="forgot-modal-text">
            We sent a 6-digit code to {maskedEmail || 'your email'}. Enter it below to continue.
          </p>
          <label className="forgot-field">
            <span>Verification code</span>
            <input
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              disabled={loading}
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
            />
          </label>
          {error && <p className="forgot-error">{error}</p>}
          <div className="forgot-actions">
            <button
              type="button"
              className="forgot-btn-secondary"
              onClick={() => {
                setStep('identify');
                setError('');
              }}
              disabled={loading}
            >
              Back
            </button>
            <button type="submit" className="forgot-btn-primary" disabled={loading}>
              {loading ? 'Verifying…' : 'Verify code'}
            </button>
          </div>
        </form>
      )}

      {step === 'reset' && (
        <form onSubmit={handleReset}>
          <p className="forgot-modal-text">Choose a new password for your account.</p>
          <label className="forgot-field">
            <span>New password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              autoFocus
              autoComplete="new-password"
            />
          </label>
          <label className="forgot-field">
            <span>Confirm new password</span>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              autoComplete="new-password"
            />
          </label>
          {error && <p className="forgot-error">{error}</p>}
          <div className="forgot-actions">
            <button type="button" className="forgot-btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="forgot-btn-primary" disabled={loading}>
              {loading ? 'Updating…' : 'Update password'}
            </button>
          </div>
        </form>
      )}

      {step === 'done' && (
        <div>
          <p className="forgot-modal-text">Your password has been updated. You can now log in with your new password.</p>
          <div className="forgot-actions">
            <button className="forgot-btn-primary" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
